import { useEffect, useState } from 'react';
import { Smartphone, Monitor, Tablet, Trash2, Power, Check, Loader2 } from 'lucide-react';
import { devices as api, ApiError } from '../lib/api.js';
import { settleProtection } from '../lib/protection.js';
import { useSession } from '../lib/session.jsx';
import { PLANS } from '../lib/config.js';
import { Glass, Button, Tag, Empty, Heading, Sheet } from './ui.jsx';

const ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };

export default function DeviceManager() {
  const { device: current, subscription, hasActiveSubscription, profile, refresh } = useSession();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [swapFor, setSwapFor] = useState(null);

  const load = () => api.list().then(setList).catch(console.error);
  useEffect(() => { load(); }, []);

  // A plan counts only while it runs; without one a single device is held.
  const limit = hasActiveSubscription ? (subscription?.device_limit ?? PLANS.basic.deviceLimit) : 1;
  const activeCount = list.filter((d) => d.is_active).length;
  const switches = profile?.switches_remaining ?? 0;

  const act = async (fn, id) => {
    setBusy(id); setError(null);
    try { await fn(); await load(); await refresh(); }
    catch (e) {
      setError(e instanceof ApiError && e.isNoSwitches ? 'No device transfers remaining this month.'
             : /before deleting/.test(e.message) ? 'Set the device dormant before deleting it.'
             : e.message);
    }
    finally { setBusy(null); setSwapFor(null); }
  };

  const activate = (d) => {
    // At the limit, a swap target is required — this is what the old app failed
    // to enforce, letting a third device onto a two-device plan.
    if (activeCount >= limit) { setSwapFor(d); return; }
    act(() => api.activate(d.id), d.id);
  };

  const deactivate = (d) => {
    if (!confirm(`Set "${d.label}" dormant? This uses one of your ${switches} transfer${switches === 1 ? '' : 's'} this month.`)) return;
    act(() => api.deactivate(d.id), d.id);
  };

  const remove = (d) => {
    if (!confirm(`Delete "${d.label}"? Its stored protection is lost.`)) return;
    act(() => api.remove(d.id), d.id);
  };

  return (
    <Glass id="devices">
      <Heading eyebrow="Bound vessels" title="Devices"
        action={<span className="text-xs font-semibold" style={{ color: 'var(--text-3)' }}>
          {activeCount}/{limit} held · {switches} transfer{switches === 1 ? '' : 's'} left
        </span>} />
      <p className="text-xs -mt-2 mb-4 leading-relaxed" style={{ color: 'var(--text-3)' }}>
        Your plan holds {limit} device{limit === 1 ? '' : 's'} at a time. Setting a device dormant — on its own or to make
        room for another — uses one of your monthly transfers. Waking a device into a free place is free.
      </p>

      {error && (
        <p className="text-sm mb-3 px-3.5 py-2.5 rounded-xl"
           style={{ color: 'var(--rust)', background: 'color-mix(in oklab, var(--rust) 12%, transparent)' }}>
          {error}
        </p>
      )}
      {list.length === 0 && <Empty>No devices registered.</Empty>}

      <ul className="divide-y" style={{ borderColor: 'var(--edge)' }}>
        {list.map((d) => {
          const Icon = ICON[d.kind] ?? Monitor;
          const isThis = d.id === current?.id;
          const { level } = settleProtection({
            level: d.protection_level, updatedAt: d.protection_updated_at,
            hasSubscription: hasActiveSubscription, deviceActive: d.is_active,
            inField: Boolean(d.checked_in_field_id),
          });
          return (
            <li key={d.id} className="flex items-center gap-3 py-3.5 first:pt-0 last:pb-0"
                style={{ borderColor: 'var(--edge)' }}>
              <span className="h-10 w-10 rounded-full grid place-items-center shrink-0"
                    style={{ background: d.is_active ? 'color-mix(in oklab, var(--clay) 16%, transparent)' : 'var(--glass-2)',
                             color: d.is_active ? 'var(--clay)' : 'var(--text-3)' }}>
                <Icon size={17} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold truncate">{d.label}</span>
                  {isThis && <Tag tone="active" dot>This device</Tag>}
                  {!d.is_active && <Tag>Dormant</Tag>}
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                  <span className="nums" style={{ color: d.is_active ? 'var(--text-2)' : undefined }}>{Math.round(level)}%</span> shielded
                  · last seen {new Date(d.last_seen_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {busy === d.id ? <Loader2 size={15} className="animate-spin" style={{ color: 'var(--text-3)' }} /> : (
                  <>
                    {d.is_active
                      ? <button title={switches > 0 ? 'Set dormant (uses a transfer)' : 'No transfers left this month'}
                                disabled={switches <= 0} onClick={() => deactivate(d)}
                                className="btn btn-glass !p-2 !rounded-full">
                          <Power size={14} />
                        </button>
                      : <button title="Wake" onClick={() => activate(d)}
                                className="btn btn-glass !p-2 !rounded-full">
                          <Check size={14} style={{ color: 'var(--sage)' }} />
                        </button>}
                    <button title={d.is_active ? 'Set dormant before deleting' : 'Delete'}
                            disabled={d.is_active} onClick={() => remove(d)}
                            className="btn btn-glass !p-2 !rounded-full">
                      <Trash2 size={14} style={{ color: 'var(--rust)' }} />
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {swapFor && (
        <SwapDialog
          target={swapFor}
          candidates={list.filter((d) => d.is_active)}
          switchesLeft={switches}
          onCancel={() => setSwapFor(null)}
          onConfirm={(victimId) => act(() => api.activate(swapFor.id, victimId), swapFor.id)}
        />
      )}
    </Glass>
  );
}

function SwapDialog({ target, candidates, switchesLeft, onCancel, onConfirm }) {
  const [pick, setPick] = useState(candidates[0]?.id ?? null);
  return (
    <Sheet onClose={onCancel}>
      <div>
        <p className="eyebrow">Device limit reached</p>
        <h3 className="display text-[1.75rem] mt-1.5">Make room for “{target.label}”</h3>
        <p className="text-sm mt-2 leading-relaxed" style={{ color: 'var(--text-2)' }}>
          Choose a device to set dormant so this one can take its place.
        </p>
      </div>
      <div className="space-y-2">
        {candidates.map((d) => {
          const Icon = ICON[d.kind] ?? Monitor;
          const on = pick === d.id;
          return (
            <button key={d.id} type="button" onClick={() => setPick(d.id)}
                    className="chip w-full flex items-center gap-3 p-3.5" data-on={on}>
              <span className="h-8 w-8 rounded-full grid place-items-center shrink-0"
                    style={{ background: 'var(--glass-2)', color: on ? 'var(--clay)' : 'var(--text-3)' }}>
                <Icon size={15} />
              </span>
              <span className="text-sm font-semibold flex-1 text-left truncate">{d.label}</span>
              {on && <Check size={15} style={{ color: 'var(--clay)' }} />}
            </button>
          );
        })}
      </div>
      <p className="text-xs" style={{ color: 'var(--text-3)' }}>
        Uses one of your {switchesLeft} remaining transfer{switchesLeft === 1 ? '' : 's'} this month.
      </p>
      <div className="flex gap-2 pt-1">
        <Button className="flex-1" disabled={!pick || switchesLeft <= 0} onClick={() => onConfirm(pick)}>
          Swap
        </Button>
        <Button variant="quiet" onClick={onCancel}>Not now</Button>
      </div>
    </Sheet>
  );
}
